import { useState, useEffect, useCallback } from "react";
import { useSiteStyles } from "@/hooks/useSiteStyles";

const STORAGE_KEY = "slh:recentColors";
const MAX_RECENT = 6;
const EVENT_NAME = "slh:recentColorsChanged";

// The only colours module instances may use — pulled live from the Site
// Styles Colour Palette (Admin → Styles). Keeps every page-builder block on
// the same brand palette instead of admins free-picking arbitrary hex values.
export const PALETTE_FIELDS = [
  { field: "primaryHex", label: "Primary" },
  { field: "secondaryHex", label: "Secondary" },
  { field: "accentHex", label: "Accent" },
  { field: "backgroundHex", label: "Site Background" },
  { field: "sectionBgHex", label: "Section Background" },
  { field: "cardBgHex", label: "Card Background" },
  { field: "textHex", label: "Body Text" },
  { field: "mutedTextHex", label: "Muted Text" },
  { field: "headingHex", label: "Headings" },
  { field: "headingAccentHex", label: "Heading Accent" },
  { field: "linkHex", label: "Links" },
  { field: "linkHoverHex", label: "Link Hover" },
  { field: "borderHex", label: "Borders" },
  { field: "headerBgHex", label: "Header Background" },
  { field: "headerTextHex", label: "Header Text" },
  { field: "headerLinkHex", label: "Header Link" },
  { field: "headerLinkHoverHex", label: "Header Link Hover" },
  { field: "headerBorderHex", label: "Header Border" },
  { field: "navBgHex", label: "Nav Background" },
  { field: "navTextHex", label: "Nav Text" },
  { field: "navHoverHex", label: "Nav Hover" },
  { field: "footerBgHex", label: "Footer Background" },
  { field: "footerTextHex", label: "Footer Text" },
  { field: "footerLinkHex", label: "Footer Link" },
  { field: "buttonBgHex", label: "Button Background" },
  { field: "buttonTextHex", label: "Button Text" },
];

function readRecent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_RECENT) : [];
  } catch {
    return [];
  }
}

function pushRecent(color) {
  if (!color || typeof color !== "string") return;
  const normalized = color.toLowerCase();
  const current = readRecent();
  const next = [normalized, ...current.filter((c) => c.toLowerCase() !== normalized)].slice(0, MAX_RECENT);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: next }));
  } catch {
    // ignore quota errors
  }
}

/**
 * ColorPickerInput — reusable color picker.
 *
 * By default (allowCustom=false) it's restricted to the Site Styles Colour
 * Palette — admins can only pick one of the brand colours defined in
 * Admin → Styles, no free hex entry. Pass allowCustom to get the full
 * native colour picker + hex input + recent-colours strip (used only by
 * the Site Styles editor itself, where the palette is actually defined).
 *
 * Props:
 *   value: string (hex)
 *   onChange: (hex) => void
 *   label?: string
 *   compact?: boolean   — when true, hide hex text input and just show swatch + recent strip
 *   allowCustom?: boolean — when true, allow free colour selection (default: false, palette-only)
 *   testId?: string
 */
export function ColorPickerInput({ value, onChange, label, compact = false, testId, allowCustom = false }) {
  const [recent, setRecent] = useState(() => readRecent());
  const { styles: siteStyles } = useSiteStyles();

  useEffect(() => {
    const handler = (e) => setRecent(e.detail || readRecent());
    const storageHandler = (e) => {
      if (e.key === STORAGE_KEY) setRecent(readRecent());
    };
    window.addEventListener(EVENT_NAME, handler);
    window.addEventListener("storage", storageHandler);
    return () => {
      window.removeEventListener(EVENT_NAME, handler);
      window.removeEventListener("storage", storageHandler);
    };
  }, []);

  const handleColorCommit = useCallback(
    (newColor) => {
      if (!newColor) return;
      onChange(newColor);
      pushRecent(newColor);
    },
    [onChange]
  );

  if (!allowCustom) {
    // Restricted mode — only the Site Styles Colour Palette is selectable.
    const palette = PALETTE_FIELDS
      .map(({ field, label: swatchLabel }) => ({ hex: siteStyles?.[field], label: swatchLabel }))
      .filter((c) => !!c.hex);
    const selectedHex = (value || "").toLowerCase();

    return (
      <div className="flex flex-col gap-1.5" data-testid={testId}>
        {label && <label className="text-xs font-medium text-muted-foreground">{label}</label>}
        <div className="flex items-center gap-1.5 flex-wrap">
          {palette.map(({ hex, label: swatchLabel }) => (
            <button
              key={swatchLabel}
              type="button"
              onClick={() => onChange(hex)}
              className={`h-7 w-7 rounded-sm border shadow-sm hover:scale-110 transition-all flex-shrink-0 ${
                selectedHex === hex.toLowerCase() ? "ring-2 ring-primary ring-offset-1 border-primary" : "border-input"
              }`}
              style={{ backgroundColor: hex }}
              aria-label={swatchLabel}
              title={`${swatchLabel} (${hex})`}
              data-testid={`palette-color-${swatchLabel.toLowerCase().replace(/\s+/g, "-")}`}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5" data-testid={testId}>
      {label && <label className="text-xs font-medium text-muted-foreground">{label}</label>}
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value || "#ffffff"}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => handleColorCommit(e.target.value)}
          className="h-9 w-12 rounded cursor-pointer border border-input flex-shrink-0"
        />
        {!compact && (
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            onBlur={(e) => handleColorCommit(e.target.value)}
            className="h-9 flex-1 min-w-0 px-2 border border-input rounded text-sm font-mono bg-background"
            placeholder="#000000"
            maxLength={7}
          />
        )}
      </div>
      {recent.length > 0 && (
        <div className="flex items-center gap-1.5 mt-1" title="Recent palette — click a swatch to reuse">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
            Palette
          </span>
          {recent.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => handleColorCommit(c)}
              className="h-5 w-5 rounded-sm border border-input shadow-sm hover:scale-110 hover:ring-2 hover:ring-primary/40 transition-all flex-shrink-0"
              style={{ backgroundColor: c }}
              aria-label={`Reuse ${c}`}
              title={c}
              data-testid={`recent-color-${c}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default ColorPickerInput;
